import { useEffect, useMemo, useRef } from 'react';
import { cn } from '@/lib/utils';
import type { SessionCallTranscriptionChatMessage } from '@/components/session-call/sessionCallTranscriptionChat';
import {
	PRONTUARIO_PANEL_PADDING_X,
	PRONTUARIO_TRANSCRIPTION_BG_CLASS,
} from '../../constants/prontuarioTheme';
import { SessionCallTranscriptionBubbleText } from './SessionCallTranscriptionBubbleText';

type SessionCallTranscriptionChatViewProps = {
	messages: SessionCallTranscriptionChatMessage[];
	transcriptionActive?: boolean;
	isProntuarioLiveCaptureEnabled?: boolean;
	stoppedNotice?: string | null;
	isPanelOpen?: boolean;
	getBaselineWordCount?: (messageId: string) => number;
	className?: string;
	scrollClassName?: string;
};

const formatMessageTime = (timestamp: string) => {
	const date = new Date(timestamp);
	if (Number.isNaN(date.getTime())) return '';
	return date.toLocaleTimeString('pt-BR', {
		hour: '2-digit',
		minute: '2-digit',
	});
};

const TRANSCRIPTION_START_LABEL = 'Início da transcrição';

const TranscriptionEmptyStateBars = () => (
	<span className="flex items-end gap-[3px]" aria-hidden>
		<span className="h-2 w-[3px] rounded-full bg-[#8ab4f8]/35" />
		<span className="h-3.5 w-[3px] rounded-full bg-[#8ab4f8]/55" />
		<span className="h-2 w-[3px] rounded-full bg-[#8ab4f8]/35" />
	</span>
);

const TranscriptionEmptyState = ({
	title,
	description,
}: {
	title: string;
	description?: string;
}) => (
	<div className="flex flex-1 items-center justify-center px-4 py-8">
		<div className="flex w-full max-w-[18rem] flex-col items-center gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.03] px-5 py-6 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
			<div className="flex size-11 items-center justify-center rounded-full bg-[#8ab4f8]/10 ring-1 ring-[#8ab4f8]/20">
				<TranscriptionEmptyStateBars />
			</div>
			<div className="space-y-1.5">
				<p className="text-[15px] font-medium leading-snug text-slate-200">
					{title}
				</p>
				{description ? (
					<p className="text-[13px] leading-relaxed text-[#9aa0a6]">
						{description}
					</p>
				) : null}
			</div>
		</div>
	</div>
);

const TranscriptionBoundaryMarker = ({ label }: { label: string }) => (
	<p
		className="w-full shrink-0 truncate px-2 py-2 text-center text-[11px] font-medium tracking-[0.08em] text-slate-500"
		role="presentation"
	>
		{label}
	</p>
);

const SessionCallTranscriptionChatView = ({
	messages,
	transcriptionActive = true,
	isProntuarioLiveCaptureEnabled = true,
	stoppedNotice = null,
	isPanelOpen = true,
	getBaselineWordCount,
	className,
	scrollClassName,
}: SessionCallTranscriptionChatViewProps) => {
	const scrollRef = useRef<HTMLDivElement>(null);
	const scrollKey = useMemo(
		() =>
			[
				messages
					.map(
						(message) =>
							`${message.id}:${message.text.length}:${message.isLive}`,
					)
					.join('|'),
				stoppedNotice ?? '',
			].join('::'),
		[messages, stoppedNotice],
	);

	useEffect(() => {
		const container = scrollRef.current;
		if (!container) return;
		container.scrollTop = container.scrollHeight - container.clientHeight;
	}, [scrollKey]);

	const emptyStateContent = (() => {
		if (!transcriptionActive) {
			return {
				title: 'Transcrição indisponível',
				description:
					'Esta sessão não está com transcrição ativa. O histórico ficará vazio.',
			};
		}
		if (!isProntuarioLiveCaptureEnabled && stoppedNotice) {
			return {
				title: 'Texto ao vivo pausado',
				description: stoppedNotice,
			};
		}
		if (!isProntuarioLiveCaptureEnabled) {
			return {
				title: 'Texto ao vivo pausado',
				description:
					'O texto ao vivo no prontuário está pausado. A transcrição oficial da consulta continua sendo salva.',
			};
		}
		return {
			title: 'Nenhuma fala transcrita ainda',
			description:
				'O histórico aparece aqui em tempo real conforme a sessão avança.',
		};
	})();

	return (
		<div
			className={cn(
				'daily-scrollbar flex h-full min-h-0 flex-col overflow-hidden',
				className,
			)}
		>
			<div
				ref={scrollRef}
				className={cn(
					'flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden py-3',
					PRONTUARIO_PANEL_PADDING_X,
					PRONTUARIO_TRANSCRIPTION_BG_CLASS,
				)}
			>
				<TranscriptionBoundaryMarker
					label={TRANSCRIPTION_START_LABEL}
				/>

				<div className="flex min-h-0 flex-1 flex-col gap-2.5">
					{messages.length === 0 ? (
						<TranscriptionEmptyState
							title={emptyStateContent.title}
							description={emptyStateContent.description}
						/>
					) : (
						messages.map((message) => (
							<div
								key={message.id}
								className={cn(
									'flex w-full',
									message.isLocalSpeaker
										? 'justify-end'
										: 'justify-start',
								)}
							>
								<div
									className={cn(
										'max-w-[85%] rounded-xl px-3 py-2',
										message.isLocalSpeaker
											? 'rounded-br-sm bg-[#8ab4f8]/10 text-slate-200'
											: 'rounded-bl-sm bg-white/[0.05] text-slate-200',
									)}
								>
									{!message.isLocalSpeaker ? (
										<p className="mb-0.5 text-[11px] font-semibold text-[#8ab4f8]/80">
											{message.speakerName}
										</p>
									) : null}
									<p className="text-[14px] leading-snug text-slate-200">
										<SessionCallTranscriptionBubbleText
											text={message.text}
											isLive={message.isLive}
											isPanelOpen={isPanelOpen}
											baselineWordCount={
												getBaselineWordCount?.(
													message.id,
												) ?? 0
											}
										/>
									</p>
									{!message.isLive ? (
										<p className="mt-1 text-right text-[10px] text-slate-500">
											{formatMessageTime(
												message.timestamp,
											)}
										</p>
									) : null}
								</div>
							</div>
						))
					)}

					{stoppedNotice ? (
						<p className="px-2 py-2 text-center text-[12px] leading-relaxed text-slate-500">
							{stoppedNotice}
						</p>
					) : null}

					{scrollClassName ? (
						<div className={scrollClassName} aria-hidden />
					) : null}
				</div>
			</div>
		</div>
	);
};

export { SessionCallTranscriptionChatView };
